import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  irrigationProblemTypeDtoSchema,
  irrigationProblemTypeListDtoSchema,
  type IrrigationProblemTypeDto,
  type IrrigationProblemTypeListDto,
} from '@lib/dto/irrigation-problem-type.dto'
import type {
  CreateIrrigationProblemTypeInput,
  UpdateIrrigationProblemTypeInput,
} from '@lib/input/irrigation-problem-type.input'
import type { IIrrigationProblemTypeService } from '@/types/IrrigationProblemTypeService.types'

/** Service for the irrigation problem directory. */
class IrrigationProblemTypeService implements IIrrigationProblemTypeService {
  async list(): Promise<IrrigationProblemTypeListDto> {
    try {
      return await api.get(
        '/irrigation-problem-types',
        irrigationProblemTypeListDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to list irrigation problems', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async create(
    input: CreateIrrigationProblemTypeInput,
  ): Promise<IrrigationProblemTypeDto> {
    try {
      return await api.post(
        '/irrigation-problem-types',
        input,
        irrigationProblemTypeDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to add an irrigation problem', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async update(
    id: string,
    input: UpdateIrrigationProblemTypeInput,
  ): Promise<IrrigationProblemTypeDto> {
    try {
      return await api.put(
        `/irrigation-problem-types/${encodeURIComponent(id)}`,
        input,
        irrigationProblemTypeDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to save an irrigation problem', {
        error: toErrorMessage(error),
        id,
      })
      throw error
    }
  }

  async archive(id: string): Promise<IrrigationProblemTypeDto> {
    try {
      return await api.post(
        `/irrigation-problem-types/${encodeURIComponent(id)}/archive`,
        {},
        irrigationProblemTypeDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to archive an irrigation problem', {
        error: toErrorMessage(error),
        id,
      })
      throw error
    }
  }

  async restore(id: string): Promise<IrrigationProblemTypeDto> {
    try {
      return await api.post(
        `/irrigation-problem-types/${encodeURIComponent(id)}/restore`,
        {},
        irrigationProblemTypeDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to restore an irrigation problem', {
        error: toErrorMessage(error),
        id,
      })
      throw error
    }
  }
}

export const irrigationProblemTypeService: IIrrigationProblemTypeService =
  new IrrigationProblemTypeService()
